import { useEffect, useState } from "react";
import Card, { SectionTitle } from "@/components/base/Card";
import Badge from "@/components/base/Badge";
import Button from "@/components/base/Button";
import { LoadingState } from "@/components/base/StateViews";
import { useToast } from "@/components/base/Toast";
import { useAuth } from "@/contexts/AuthContext";
import { getCycles, openCycle } from "@/services/schoolService";
import { CYCLE_SCOPE_LABEL } from "@/utils/cycleScope";
import type { Cycle, CycleCode } from "@/types";

const CYCLE_CODES: CycleCode[] = ["PRIMAIRE", "SECONDAIRE"];

/**
 * Cycles de l'école : en ouvrir un crée ses matières et catégories financières de départ, aucune classe (et
 * l'année scolaire en cours si l'école n'en a pas). Les limites du forfait s'appliquent côté serveur.
 */
export default function CyclesPanel() {
  const { notify } = useToast();
  const { can } = useAuth();
  const [cycles, setCycles] = useState<Cycle[] | null>(null);
  const [busy, setBusy] = useState<CycleCode | null>(null);

  useEffect(() => {
    getCycles().then(setCycles);
  }, []);

  if (!cycles) return <LoadingState />;

  const open = async (code: CycleCode) => {
    setBusy(code);
    try {
      const result = await openCycle(code);
      notify(
        `Cycle ${CYCLE_SCOPE_LABEL[code]} ouvert (${result.subjects} matières, année ${result.schoolYear}). Créez maintenant ses classes.`,
      );
      // Menu, sélecteur d'année et listes de cycles se rechargent avec les nouvelles données.
      setTimeout(() => window.location.reload(), 1200);
    } catch (err) {
      notify(err instanceof Error ? err.message : "Ouverture du cycle impossible.", "error");
      setBusy(null);
    }
  };

  return (
    <Card>
      <SectionTitle icon="ri-stack-line" title="Cycles" subtitle="Primaire et Secondaire de l'école" />
      <p className="mb-4 text-sm text-foreground-600">
        Ouvrir un cycle crée ses matières de départ, ses catégories de recettes et de dépenses, son rôle, et l'année
        scolaire en cours si l'école n'en a pas encore. Aucune classe n'est créée : ajoutez vous-même les vôtres dans
        la page Classes.
      </p>
      <ul className="divide-y divide-background-200 rounded-lg border border-background-200">
        {CYCLE_CODES.map((code) => {
          const cycle = cycles.find((c) => c.code === code);
          return (
            <li key={code} className="flex flex-wrap items-center justify-between gap-3 px-4 py-3">
              <span className="flex items-center gap-2 text-sm font-medium text-foreground-900">
                <i className="ri-graduation-cap-line text-foreground-500" aria-hidden="true"></i>
                {CYCLE_SCOPE_LABEL[code]}
              </span>
              {cycle ? (
                <Badge tone="success" icon="ri-check-line">
                  Ouvert
                </Badge>
              ) : can("cycles:create") ? (
                <Button size="sm" icon="ri-add-line" onClick={() => open(code)} disabled={busy !== null}>
                  {busy === code ? "Ouverture..." : "Ouvrir ce cycle"}
                </Button>
              ) : (
                <Badge tone="neutral">Non ouvert</Badge>
              )}
            </li>
          );
        })}
      </ul>
    </Card>
  );
}
